import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ExternalLink, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

export default function Payouts() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [params] = useSearchParams();

  const load = () =>
    api.get("/connect/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ connected: false }))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    if (params.get("connect") === "return") toast.success("You've returned from Stripe onboarding.");
  }, []); // eslint-disable-line

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding");
      setStarting(false);
    }
  };

  const openDashboard = async () => {
    try {
      const { data } = await api.post("/connect/dashboard-link");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open Stripe dashboard");
    }
  };

  if (loading) return <div className="mx-auto max-w-3xl py-24 text-center text-muted-foreground">Loading payout status…</div>;

  const canOnboard = user?.user_type === "business" || user?.user_type === "removal_company";
  const s = status || { connected: false };
  const ready = s.charges_enabled && s.payouts_enabled;

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12">
      <div className="overline text-safety">Payouts</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter text-forest">
        Get paid via Stripe
      </h1>
      <p className="mt-3 text-muted-foreground max-w-xl">
        Connect a Stripe Express account to accept payouts for clearances and sales.
        Stripe handles KYC, banking and payouts in ~2 days. <b>No platform fee</b> on any user-to-user transaction.
      </p>

      {!canOnboard ? (
        <div className="mt-8 border border-border bg-white p-8">
          <div className="overline text-muted-foreground">Not eligible</div>
          <div className="mt-2 font-display text-xl text-forest">Payouts are for businesses and removal firms only.</div>
        </div>
      ) : !s.connected ? (
        <div className="mt-8 border border-border bg-white p-8" data-testid="payouts-not-connected">
          <div className="flex items-start gap-4">
            <CreditCard className="w-8 h-8 text-safety shrink-0" />
            <div className="flex-1">
              <div className="font-display text-xl text-forest">Not connected yet</div>
              <p className="mt-2 text-sm text-muted-foreground">
                Set up your Stripe Express account to receive payouts, verify your business, and unlock buyer trust.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-forest">
                <li>✓ Verified business identity (auto-KYC)</li>
                <li>✓ 2-day bank transfers</li>
                <li>✓ Fraud protection + dispute handling by Stripe</li>
                <li>✓ Access to advertiser directory once we launch it</li>
              </ul>
              <Button
                onClick={start}
                disabled={starting}
                data-testid="payouts-start-btn"
                className="mt-6 rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8"
              >
                {starting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Stripe…</>) : "Set up Stripe payouts →"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 border-2 border-forest bg-sage-light p-8" data-testid="payouts-connected">
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-8 h-8 text-forest shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-display text-2xl tracking-tight text-forest">Stripe connected</div>
                {ready ? (
                  <Badge className="rounded-none bg-forest text-white overline">Ready to accept payouts</Badge>
                ) : (
                  <Badge className="rounded-none bg-safety text-white overline">Onboarding incomplete</Badge>
                )}
              </div>
              <div className="mt-2 text-xs text-muted-foreground font-mono truncate">Account: {s.account_id}</div>

              {!ready && (s.currently_due?.length > 0 || s.past_due?.length > 0) && (
                <div className="mt-4 border border-safety bg-white p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-safety shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-medium">Stripe still needs some info from you.</div>
                    <div className="mt-1 text-muted-foreground text-xs">
                      {(s.past_due || []).concat(s.currently_due || []).slice(0, 5).join(" · ")}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                {!ready && (
                  <Button
                    onClick={start}
                    disabled={starting}
                    data-testid="payouts-continue-btn"
                    className="rounded-none bg-safety hover:bg-safety-hover text-white h-11 px-6"
                  >
                    Continue onboarding →
                  </Button>
                )}
                <Button
                  onClick={openDashboard}
                  variant="outline"
                  data-testid="payouts-dashboard-btn"
                  className="rounded-none border-forest text-forest hover:bg-forest hover:text-white h-11 px-6"
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open Stripe dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10">
        <Link to="/dashboard" className="overline text-xs text-safety hover:text-forest">← Back to dashboard</Link>
      </div>
    </div>
  );
}
